"use client";

import { useEffect, useState } from "react";
import {
  Timestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { completionSchema, type Completion } from "@/lib/schemas";
import { todayMidnight } from "@/lib/utils/dates";
import { markReady } from "@/lib/realtime/registry";

type State = { completions: Completion[]; loading: boolean };

const cache = new Map<string, Completion[]>();
const cacheKey = (hid: string, uid: string) => `${hid}|${uid}`;
export const todaysCompletionsKey = (hid: string, uid: string) =>
  `todays:${hid}:${uid}`;

/**
 * Dzisiejsze completions zalogowanego usera. Realtime + module-level cache
 * (brak migotania "Ładowanie..." po zmianie tab).
 */
export function useTodaysCompletions(
  householdId: string | undefined,
  userId: string | undefined,
): State {
  const key = householdId && userId ? cacheKey(householdId, userId) : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    completions: cached ?? [],
    loading: cached === undefined,
  });

  useEffect(() => {
    if (!householdId || !userId) return;
    const db = getDb();
    const today = Timestamp.fromDate(todayMidnight());
    const q = query(
      refCol(db, paths.completions(householdId)),
      where("completedBy", "==", userId),
      where("dueDate", "==", today),
    );
    const k = cacheKey(householdId, userId);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const completions = snap.docs.flatMap((d) => {
          const parsed = completionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(k, completions);
        markReady(todaysCompletionsKey(householdId, userId));
        setState({ completions, loading: false });
      },
      () => setState({ completions: [], loading: false }),
    );
    return () => unsub();
  }, [householdId, userId]);

  return state;
}
